import { useState } from 'react'
import { Loader2, AlertTriangle, Folder, File, Link2 } from 'lucide-react'
import { previewShareExtendItems, type ExtendPreviewItem } from '../client/index.js'

/**
 * (0085) "Your access extends this share" — shown when the caller's reach covers things
 * the link's contributors collectively can't (a folder the creator couldn't share, a
 * `.chain` placed after the link was made). Preview lists WHAT would become visible
 * before anything is extended (decision 2026-10-01).
 */
export function ExtendAccessCard({ linkId, newlyCoveredEntityCount, extending, onExtend }: {
  linkId: string
  newlyCoveredEntityCount: number
  extending: boolean
  onExtend: () => void
}) {
  const [preview, setPreview] = useState<{ items: ExtendPreviewItem[]; truncated: boolean } | 'loading' | 'error' | null>(null)
  const togglePreview = async () => {
    if (preview && preview !== 'loading') { setPreview(null); return }
    setPreview('loading')
    try {
      const res = await previewShareExtendItems(linkId)
      setPreview({ items: res.items ?? [], truncated: res.itemsTruncated === true })
    } catch {
      setPreview('error')
    }
  }
  const open = preview !== null && preview !== 'loading'
  return (
    <div className="mx-3 p-3 rounded-md border border-amber-500/40 bg-amber-500/5">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium themed-text">Your access extends this share</div>
          <p className="text-[11px] themed-text-muted leading-relaxed mt-0.5">
            You can reach {newlyCoveredEntityCount} {newlyCoveredEntityCount === 1 ? 'item' : 'items'} inside this share that recipients currently can't.
            Click below to grant recipients the same reach.
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => void togglePreview()}
            disabled={preview === 'loading'}
            className="themed-btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md disabled:opacity-50"
            title="See what would become visible on this link"
          >
            {preview === 'loading' && <Loader2 size={11} className="animate-spin" />}
            {open ? 'Hide preview' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={onExtend}
            disabled={extending}
            className="themed-btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md disabled:opacity-50"
            title="Add your access to this share"
          >
            {extending && <Loader2 size={11} className="animate-spin" />}
            Extend access
          </button>
        </div>
      </div>
      {preview === 'error' && (
        <p className="text-[11px] text-red-400 mt-2">Couldn't load the preview. Try again.</p>
      )}
      {preview && typeof preview === 'object' && (
        <div className="mt-2 pl-7">
          <div className="text-[11px] themed-text-muted mb-1">Recipients would newly see:</div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {preview.items.map((it) => (
              <li key={it.id} className="flex items-start gap-2 text-[11px]">
                <span className="mt-0.5 themed-text-muted shrink-0">
                  {it.kind === 'shortcut' ? <Link2 size={12} /> : it.kind === 'folder' ? <Folder size={12} /> : <File size={12} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="themed-text truncate">{it.name}</span>
                    <span className="px-1 rounded border themed-border themed-text-muted text-[10px] shrink-0">{it.level === 'edit' ? 'Edit' : 'View'}</span>
                  </div>
                  {it.kind === 'shortcut' && (
                    <div className="themed-text-muted">
                      {`Shortcut to ${it.targetName ?? 'another folder'}${it.inside === undefined ? '' : ` · ${it.inside} ${it.inside === 1 ? 'item' : 'items'}`}`}
                    </div>
                  )}
                  {it.kind === 'folder' && (it.inside ?? 0) > 0 && (
                    <div className="themed-text-muted">{`${it.inside} ${it.inside === 1 ? 'item' : 'items'} inside`}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {preview.truncated && (
            <p className="text-[11px] themed-text-muted mt-1">Showing the first items only.</p>
          )}
        </div>
      )}
    </div>
  )
}
